import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, getErrorMessage } from "@/lib/api";
import { WaveIcon } from "@/components/landing/Decorations";
import { toast } from "sonner";
import {
    LayoutDashboard, Users, FileAudio, BarChart3, LogOut,
    Search, RefreshCw, Loader2,
    Ban, Check, ShieldCheck, CreditCard, Trash2, Plus,
    Minus, Eye, ArrowLeft, Shield, AlertTriangle, Zap,
    TrendingUp, Activity, X, Tag, LifeBuoy, Lock, Wallet,
    CheckCircle2, Clock, AlertCircle, XCircle, ChevronLeft, ChevronRight, Key, Globe, DollarSign,
} from "lucide-react";

const PLAN_COLORS = {
    free: "bg-gray-100 text-gray-700",
    starter: "bg-purple-100 text-purple-700",
    pro: "bg-coral/20 text-coral",
    business: "bg-mint/30 text-ink",
    professional: "bg-coral/20 text-coral",
    basic: "bg-purple-100 text-purple-700",
};
const TABS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "users", label: "Users", icon: Users },
    { id: "articles", label: "Articles", icon: FileAudio },
    { id: "payments", label: "Payments", icon: Wallet },
    { id: "coupons", label: "Coupons", icon: Tag },
    { id: "support", label: "Support", icon: LifeBuoy },
    { id: "security", label: "Security", icon: Lock },
    { id: "api_keys", label: "API Keys", icon: Key },
    { id: "monetize", label: "Monetize", icon: DollarSign },
];

const fmtChars = (n) =>
    n >= 1000000 ? `${(n/1000000).toFixed(1).replace(/\.0$/,"")  }M`
    : n >= 1000 ? `${(n/1000).toFixed(1).replace(/\.0$/,"")}k`
    : String(n ?? 0);

// ─── Dashboard tab ────────────────────────────────────────────
function DashboardTab() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { setStats((await api.get("/admin/stats")).data); }
        catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex items-center gap-3 p-10 text-ink-muted"><Loader2 className="animate-spin" size={20}/> Loading stats…</div>;
    if (!stats) return null;

    const statCards = [
        { label: "Total users", value: stats.total_users, icon: Users, color: "text-purple-500" },
        { label: "Active users", value: stats.active_users, icon: Activity, color: "text-mint" },
        { label: "Blocked users", value: stats.blocked_users, icon: Ban, color: "text-red-500" },
        { label: "New this week", value: stats.new_users_week, icon: TrendingUp, color: "text-blue-500" },
        { label: "Paid subscribers", value: stats.paid_users, icon: CreditCard, color: "text-coral" },
        { label: "Total articles", value: stats.total_articles, icon: FileAudio, color: "text-ink" },
        { label: "Ready articles", value: stats.ready_articles, icon: Check, color: "text-mint" },
        { label: "Total plays", value: stats.total_plays, icon: BarChart3, color: "text-purple-500" },
        { label: "Chars generated", value: fmtChars(stats.total_chars_generated), icon: Zap, color: "text-coral", raw: true },
        { label: "Top-up purchases", value: stats.topup_purchases, icon: CreditCard, color: "text-mint" },
        { label: "Revenue (BDT)", value: `৳${(stats.total_revenue_bdt || 0).toLocaleString()}`, icon: TrendingUp, color: "text-coral", raw: true },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">Platform Overview</h2>
                <button onClick={load} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {statCards.map(card => {
                    const Icon = card.icon;
                    return (
                        <div key={card.label} className="bg-white rounded-2xl p-5 border border-cream-300 shadow-soft">
                            <div className={`mb-3 ${card.color}`}><Icon size={18}/></div>
                            <p className="font-display text-[28px] font-bold leading-none">
                                {card.raw ? card.value : (card.value ?? 0).toLocaleString()}
                            </p>
                            <p className="text-[11px] text-ink-muted mt-1 uppercase tracking-[0.15em] font-semibold">{card.label}</p>
                        </div>
                    );
                })}
            </div>

            {/* Plan breakdown */}
            <div className="bg-white rounded-2xl p-6 border border-cream-300 shadow-soft">
                <h3 className="font-semibold text-[15px] mb-4">Users by plan</h3>
                <div className="flex flex-wrap gap-3">
                    {Object.entries(stats.plan_breakdown || {}).map(([plan, count]) => (
                        <div key={plan} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold ${PLAN_COLORS[plan] || "bg-gray-100 text-gray-700"}`}>
                            <span className="capitalize">{plan}</span>
                            <span className="font-mono font-bold">{count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── User detail modal ────────────────────────────────────────
function UserModal({ uid, onClose }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creditsInput, setCreditsInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [pwSaving, setPwSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { setUser((await api.get(`/admin/users/${uid}`)).data); }
        catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, [uid]);
    useEffect(() => { load(); }, [load]);

    const patch = async (updates) => {
        setSaving(true);
        try {
            await api.patch(`/admin/users/${uid}`, updates);
            toast.success("Updated");
            await load();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setSaving(false); }
    };

    const deleteUser = async () => {
        if (!window.confirm(`Permanently delete ${user?.email}? This removes the user, all their articles, credits, and purchase history. This cannot be undone.`)) return;
        // second confirmation
        const typed = window.prompt(`Type the user's email to confirm deletion:`);
        if (typed?.trim() !== user?.email) { toast.error("Email didn't match. Deletion cancelled."); return; }
        setSaving(true);
        try {
            await api.delete(`/admin/users/${uid}`);
            toast.success("User and all data deleted");
            onClose();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setSaving(false); }
    };

    const adjustCredits = async (sign) => {
        const val = parseInt(creditsInput, 10);
        if (!val || isNaN(val)) { toast.error("Enter a valid number of chars"); return; }
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/users/${uid}/credits`, { chars: sign * val });
            toast.success(`Credits updated — monthly: ${fmtChars(data.monthly_chars)}, top-up: ${fmtChars(data.topup_chars)}`);
            setCreditsInput("");
            await load();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setSaving(false); }
    };

    const setPassword = async () => {
        if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        setPwSaving(true);
        try {
            await api.post(`/admin/users/${uid}/set-password`, { new_password: newPassword });
            toast.success("Password updated");
            setNewPassword("");
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setPwSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-10 px-4" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-cream-300 px-7 py-5 flex items-center justify-between rounded-t-3xl">
                    <h3 className="font-display text-[20px] font-semibold">User detail</h3>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-cream-100 transition"><X size={16}/></button>
                </div>
                {loading ? (
                    <div className="p-10 flex items-center gap-3 text-ink-muted"><Loader2 className="animate-spin" size={18}/> Loading…</div>
                ) : user ? (
                    <div className="p-7 space-y-6">
                        {/* Identity */}
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-coral/20 flex items-center justify-center font-display text-[22px] font-bold text-coral">
                                {(user.name || user.email || "?")[0].toUpperCase()}
                            </div>
                            <div>
                                <p className="font-semibold text-[16px]">{user.name}</p>
                                <p className="text-[13px] text-ink-muted">{user.email}</p>
                                <p className="text-[11px] text-ink-muted font-mono mt-0.5">{user.user_id}</p>
                            </div>
                            <div className="ml-auto flex gap-2">
                                {user.is_blocked && <span className="text-[11px] font-bold bg-red-100 text-red-600 px-2.5 py-1 rounded-full uppercase tracking-wide">Blocked</span>}
                                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${PLAN_COLORS[user.plan] || "bg-gray-100 text-gray-700"}`}>{user.plan}</span>
                            </div>
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-4 gap-3">
                            {[
                                { label: "Articles", value: user.article_count },
                                { label: "Plays", value: user.play_count },
                                { label: "Monthly chars", value: fmtChars(user.balance?.monthly_chars) },
                                { label: "Top-up chars", value: fmtChars(user.balance?.topup_chars) },
                            ].map(s => (
                                <div key={s.label} className="bg-cream-100 rounded-xl p-3 text-center">
                                    <p className="font-bold text-[18px] font-display">{s.value ?? 0}</p>
                                    <p className="text-[10px] text-ink-muted uppercase tracking-wide">{s.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Plan change */}
                        <div className="bg-cream-100 rounded-2xl p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Change plan</p>
                            <div className="flex flex-wrap gap-2">
                                {["free", "starter", "pro", "business"].map(p => (
                                    <button key={p} onClick={() => patch({ plan: p })} disabled={saving || user.plan === p}
                                        className={`px-3 py-1.5 rounded-xl text-[12px] font-semibold transition capitalize ${user.plan === p ? "bg-ink text-cream-100 cursor-default" : "bg-white border border-cream-300 hover:bg-coral hover:text-white hover:border-coral disabled:opacity-50"}`}>
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Role change */}
                        <div className="bg-cream-100 rounded-2xl p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Role</p>
                            <div className="flex gap-2">
                                {["user", "editor", "admin"].map(r => (
                                    <button key={r} onClick={() => patch({ role: r })} disabled={saving || user.role === r}
                                        className={`px-3 py-1.5 rounded-xl text-[12px] font-semibold transition capitalize ${user.role === r ? "bg-ink text-cream-100 cursor-default" : "bg-white border border-cream-300 hover:bg-ink hover:text-cream-100 disabled:opacity-50"}`}>
                                        {r}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Credits adjustment */}
                        <div className="bg-cream-100 rounded-2xl p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Adjust credits (chars)</p>
                            <div className="flex gap-2">
                                <input
                                    type="number" value={creditsInput} onChange={e => setCreditsInput(e.target.value)}
                                    placeholder="e.g. 50000"
                                    className="flex-1 bg-white border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                                />
                                <button onClick={() => adjustCredits(1)} disabled={saving}
                                    className="px-4 py-2 rounded-xl bg-mint text-ink text-[12px] font-semibold flex items-center gap-1.5 hover:opacity-80 disabled:opacity-50">
                                    <Plus size={12}/> Add
                                </button>
                                <button onClick={() => adjustCredits(-1)} disabled={saving}
                                    className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 text-[12px] font-semibold flex items-center gap-1.5 hover:opacity-80 disabled:opacity-50">
                                    <Minus size={12}/> Deduct
                                </button>
                            </div>
                            <p className="text-[11px] text-ink-muted mt-2">Adding goes to top-up balance. Deducting removes from monthly first.</p>
                        </div>

                        {/* Set password */}
                        <div className="bg-cream-100 rounded-2xl p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Set new password</p>
                            <div className="flex gap-2">
                                <input
                                    type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                    placeholder="New password (min 6 chars)"
                                    className="flex-1 bg-white border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                                />
                                <button onClick={setPassword} disabled={pwSaving || newPassword.length < 6}
                                    className="px-4 py-2 rounded-xl bg-ink text-cream-100 text-[12px] font-semibold hover:opacity-80 disabled:opacity-40 transition">
                                    {pwSaving ? "Saving…" : "Set password"}
                                </button>
                            </div>
                        </div>

                        {/* Block / Unblock */}
                        <div className="flex gap-3">
                            <button
                                onClick={() => patch({ is_blocked: !user.is_blocked })} disabled={saving}
                                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-[13px] font-semibold transition ${
                                    user.is_blocked
                                        ? "bg-mint/20 text-ink hover:bg-mint/40"
                                        : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                                }`}>
                                {user.is_blocked ? <><ShieldCheck size={14}/> Unblock user</> : <><Ban size={14}/> Block user</>}
                            </button>
                        </div>

                        {/* Danger zone */}
                        <div className="border border-red-200 rounded-2xl p-5">
                            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-red-500 mb-1 flex items-center gap-1.5"><AlertTriangle size={12}/> Danger zone</p>
                            <p className="text-[12px] text-ink-muted mb-4">Permanently deletes this user plus all their articles, audio files, credits, and purchase history. Cannot be undone.</p>
                            <button
                                onClick={deleteUser} disabled={saving}
                                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-semibold transition disabled:opacity-50">
                                <Trash2 size={13}/> Delete user permanently
                            </button>
                        </div>

                        {/* Recent articles */}
                        {user.recent_articles?.length > 0 && (
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-3">Recent articles</p>
                                <div className="space-y-2">
                                    {user.recent_articles.map(a => (
                                        <div key={a.id} className="flex items-center gap-3 bg-cream-100 rounded-xl px-4 py-2.5">
                                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${a.status === "ready" ? "bg-mint" : a.status === "error" ? "bg-red-400" : "bg-amber-400"}`}/>
                                            <span className="text-[13px] flex-1 truncate">{a.title}</span>
                                            <span className="text-[11px] text-ink-muted">{(a.created_at || "").slice(0, 10)}</span>
                                            <span className="text-[11px] text-ink-muted">{a.plays} plays</span>
                                            <button
                                                onClick={async () => {
                                                    if (!window.confirm("Delete this article?")) return;
                                                    try {
                                                        await api.delete(`/admin/articles/${a.id}`);
                                                        toast.success("Article deleted");
                                                        await load();
                                                    } catch (e) { toast.error(getErrorMessage(e)); }
                                                }}
                                                className="p-1 rounded-lg hover:bg-red-100 text-ink-muted hover:text-red-600 transition flex-shrink-0"
                                                title="Delete article">
                                                <Trash2 size={12}/>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : <div className="p-10 text-ink-muted text-center">User not found</div>}
            </div>
        </div>
    );
}

// ─── Users tab ────────────────────────────────────────────────
function UsersTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [planFilter, setPlanFilter] = useState("");
    const [blockedFilter, setBlockedFilter] = useState("");
    const [page, setPage] = useState(0);
    const [selectedUid, setSelectedUid] = useState(null);
    const LIMIT = 20;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: LIMIT, skip: page * LIMIT });
            if (search) params.set("search", search);
            if (planFilter) params.set("plan", planFilter);
            if (blockedFilter) params.set("blocked", blockedFilter);
            setData((await api.get(`/admin/users?${params}`)).data);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, [search, planFilter, blockedFilter, page]);

    useEffect(() => { load(); }, [load]);

    const quickPatch = async (uid, updates) => {
        try {
            await api.patch(`/admin/users/${uid}`, updates);
            toast.success("Updated");
            await load();
        } catch (e) { toast.error(getErrorMessage(e)); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-[26px] font-semibold tracking-tight flex-1">Users</h2>
                <button onClick={load} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"/>
                    <input
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        placeholder="Search name or email…"
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-cream-300 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                    />
                </div>
                <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(0); }}
                    className="bg-white border border-cream-300 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none">
                    <option value="">All plans</option>
                    {["free","starter","pro","business"].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={blockedFilter} onChange={e => { setBlockedFilter(e.target.value); setPage(0); }}
                    className="bg-white border border-cream-300 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none">
                    <option value="">All status</option>
                    <option value="false">Active</option>
                    <option value="true">Blocked</option>
                </select>
            </div>

            <p className="text-[12px] text-ink-muted">{data ? `${data.total} users found` : "Loading…"}</p>

            <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-hidden">
                {loading ? (
                    <div className="p-8 flex items-center gap-3 text-ink-muted"><Loader2 className="animate-spin" size={18}/> Loading users…</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-cream-100 border-b border-cream-300">
                                <tr>
                                    {["User","Plan","Articles","Chars remaining","Status","Actions"].map(h => (
                                        <th key={h} className="px-4 py-3 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-ink-muted whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-cream-300">
                                {(data?.users || []).map(u => {
                                    const totalChars = (u.monthly_chars_remaining || 0) + (u.topup_chars_remaining || 0);
                                    return (
                                        <tr key={u.user_id} className="hover:bg-cream-100/50 transition">
                                            <td className="px-4 py-3">
                                                <p className="text-[13px] font-semibold text-ink">{u.name}</p>
                                                <p className="text-[11px] text-ink-muted">{u.email}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${PLAN_COLORS[u.plan] || "bg-gray-100 text-gray-700"}`}>{u.plan}</span>
                                            </td>
                                            <td className="px-4 py-3 text-[13px] text-ink">{u.article_count}</td>
                                            <td className="px-4 py-3 text-[13px] font-mono text-ink">{fmtChars(totalChars)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${u.is_blocked ? "bg-red-100 text-red-600" : "bg-mint/30 text-ink"}`}>
                                                    {u.is_blocked ? "Blocked" : "Active"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => setSelectedUid(u.user_id)}
                                                        className="p-1.5 rounded-lg hover:bg-cream-200 text-ink-muted hover:text-ink transition" title="View detail">
                                                        <Eye size={13}/>
                                                    </button>
                                                    <button onClick={() => quickPatch(u.user_id, { is_blocked: !u.is_blocked })}
                                                        className={`p-1.5 rounded-lg transition ${u.is_blocked ? "hover:bg-mint/20 text-mint" : "hover:bg-red-100 text-red-400 hover:text-red-600"}`}
                                                        title={u.is_blocked ? "Unblock" : "Block"}>
                                                        {u.is_blocked ? <ShieldCheck size={13}/> : <Ban size={13}/>}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (!window.confirm(`Delete ${u.email}? This is permanent.`)) return;
                                                            try {
                                                                await api.delete(`/admin/users/${u.user_id}`);
                                                                toast.success("User deleted");
                                                                await load();
                                                            } catch (e) { toast.error(getErrorMessage(e)); }
                                                        }}
                                                        className="p-1.5 rounded-lg hover:bg-red-100 text-ink-muted hover:text-red-600 transition"
                                                        title="Delete user">
                                                        <Trash2 size={13}/>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!data?.users?.length && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-muted text-[14px]">No users found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {data && data.total > LIMIT && (
                <div className="flex items-center justify-between">
                    <p className="text-[12px] text-ink-muted">Page {page + 1} of {Math.ceil(data.total / LIMIT)}</p>
                    <div className="flex gap-2">
                        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            className="px-4 py-2 rounded-xl border border-cream-300 text-[12px] hover:bg-cream-100 disabled:opacity-40 transition">← Prev</button>
                        <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * LIMIT >= data.total}
                            className="px-4 py-2 rounded-xl border border-cream-300 text-[12px] hover:bg-cream-100 disabled:opacity-40 transition">Next →</button>
                    </div>
                </div>
            )}

            {selectedUid && <UserModal uid={selectedUid} onClose={() => setSelectedUid(null)}/>}
        </div>
    );
}

// ─── Articles tab ─────────────────────────────────────────────
function ArticlesTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(0);
    const [deleting, setDeleting] = useState(null);
    const LIMIT = 30;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: LIMIT, skip: page * LIMIT });
            if (search) params.set("search", search);
            if (statusFilter) params.set("status", statusFilter);
            setData((await api.get(`/admin/articles?${params}`)).data);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, [search, statusFilter, page]);

    useEffect(() => { load(); }, [load]);

    const deleteArticle = async (aid) => {
        if (!window.confirm("Delete this article permanently?")) return;
        setDeleting(aid);
        try {
            await api.delete(`/admin/articles/${aid}`);
            toast.success("Article deleted");
            await load();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setDeleting(null); }
    };

    const STATUS_COLORS = {
        ready: "bg-mint/30 text-ink",
        processing: "bg-amber-100 text-amber-700",
        error: "bg-red-100 text-red-600",
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-[26px] font-semibold tracking-tight flex-1">Articles</h2>
                <button onClick={load} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"/>
                    <input
                        value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                        placeholder="Search article title…"
                        className="w-full pl-9 pr-4 py-2.5 bg-white border border-cream-300 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                    />
                </div>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                    className="bg-white border border-cream-300 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none">
                    <option value="">All statuses</option>
                    {["ready","processing","error"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <p className="text-[12px] text-ink-muted">{data ? `${data.total} articles found` : "Loading…"}</p>

            <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-hidden">
                {loading ? (
                    <div className="p-8 flex items-center gap-3 text-ink-muted"><Loader2 className="animate-spin" size={18}/> Loading articles…</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-cream-100 border-b border-cream-300">
                                <tr>
                                    {["Title","User","Voice","Status","Plays","Date","Actions"].map(h => (
                                        <th key={h} className="px-4 py-3 text-[10.5px] uppercase tracking-[0.18em] font-semibold text-ink-muted whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-cream-300">
                                {(data?.articles || []).map(a => (
                                    <tr key={a.id} className="hover:bg-cream-100/50 transition">
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <p className="text-[13px] font-semibold text-ink truncate">{a.title}</p>
                                            <p className="text-[11px] font-mono text-ink-muted">{a.id}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-[12px] text-ink">{a.user?.name || "—"}</p>
                                            <p className="text-[11px] text-ink-muted">{a.user?.email || "—"}</p>
                                            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${PLAN_COLORS[a.user?.plan] || "bg-gray-100 text-gray-600"}`}>{a.user?.plan}</span>
                                        </td>
                                        <td className="px-4 py-3 text-[12px] text-ink">{a.voice_id}</td>
                                        <td className="px-4 py-3">
                                            <span className={`text-[10.5px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLORS[a.status] || "bg-gray-100 text-gray-600"}`}>{a.status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-[13px] text-ink">{a.plays ?? 0}</td>
                                        <td className="px-4 py-3 text-[12px] text-ink-muted whitespace-nowrap">{(a.created_at || "").slice(0, 10)}</td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => deleteArticle(a.id)} disabled={deleting === a.id}
                                                className="p-1.5 rounded-lg hover:bg-red-100 text-ink-muted hover:text-red-600 transition">
                                                {deleting === a.id ? <Loader2 size={13} className="animate-spin"/> : <Trash2 size={13}/>}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!data?.articles?.length && (
                                    <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted text-[14px]">No articles found</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {data && data.total > LIMIT && (
                <div className="flex items-center justify-between">
                    <p className="text-[12px] text-ink-muted">Page {page + 1} of {Math.ceil(data.total / LIMIT)}</p>
                    <div className="flex gap-2">
                        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                            className="px-4 py-2 rounded-xl border border-cream-300 text-[12px] hover:bg-cream-100 disabled:opacity-40 transition">← Prev</button>
                        <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * LIMIT >= data.total}
                            className="px-4 py-2 rounded-xl border border-cream-300 text-[12px] hover:bg-cream-100 disabled:opacity-40 transition">Next →</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Main admin panel ─────────────────────────────────────────
// ─── Coupons tab ────────────────────────────────────────────
const PLAN_KEYS = ["all", "custom", "free", "starter", "basic", "pro", "professional", "business"];
const BUNDLE_KEYS = ["starter_mini", "starter_small", "starter_large", "premium_mini", "premium_small", "premium_large"];
const ALL_APPLICABLE = [...PLAN_KEYS, ...BUNDLE_KEYS];

const EMPTY_COUPON = {
    code: "",
    discount_type: "percentage",
    discount_value: "",
    applicable_to: ["all"],
    max_uses: 0,
    active: true,
};

function CouponsTab() {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_COUPON);
    const [saving, setSaving] = useState(false);
    const [deletingCode, setDeletingCode] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { setCoupons((await api.get("/admin/coupons")).data); }
        catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleApplicable = (key) => {
        setForm(f => {
            if (key === "all") return { ...f, applicable_to: ["all"] };
            const cur = f.applicable_to.filter(x => x !== "all");
            const next = cur.includes(key) ? cur.filter(x => x !== key) : [...cur, key];
            return { ...f, applicable_to: next.length === 0 ? ["all"] : next };
        });
    };

    const handleCreate = async () => {
        if (!form.code.trim()) { toast.error("Code is required"); return; }
        if (!form.discount_value || isNaN(parseFloat(form.discount_value))) { toast.error("Enter a valid discount value"); return; }
        if (form.applicable_to.length === 0) { toast.error("Select at least one applicable product"); return; }
        setSaving(true);
        try {
            await api.post("/admin/coupons", {
                ...form,
                code: form.code.trim().toUpperCase(),
                discount_value: parseFloat(form.discount_value),
                max_uses: parseInt(form.max_uses, 10) || 0,
            });
            toast.success("Coupon created!");
            setForm(EMPTY_COUPON);
            load();
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (code, current) => {
        try {
            await api.patch(`/admin/coupons/${code}`, { active: !current });
            setCoupons(cs => cs.map(c => c.code === code ? { ...c, active: !current } : c));
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
    };

    const handleDelete = async (code) => {
        if (!window.confirm(`Delete coupon ${code}?`)) return;
        setDeletingCode(code);
        try {
            await api.delete(`/admin/coupons/${code}`);
            toast.success(`Coupon ${code} deleted`);
            setCoupons(cs => cs.filter(c => c.code !== code));
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setDeletingCode(null);
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">Coupons</h2>
                <button onClick={load} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            {/* Create form */}
            <div className="bg-white rounded-2xl border border-cream-300 shadow-soft p-6">
                <h3 className="font-semibold text-[15px] mb-5">Create coupon</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Code</label>
                        <input
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[13px] font-mono uppercase focus:outline-none focus:ring-2 focus:ring-coral/40"
                            placeholder="WELCOME20"
                            value={form.code}
                            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Type</label>
                        <select
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                            value={form.discount_type}
                            onChange={e => setForm(f => ({ ...f, discount_type: e.target.value }))}
                        >
                            <option value="percentage">Percentage (%)</option>
                            <option value="fixed">Fixed (BDT ৳)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">
                            {form.discount_type === "percentage" ? "Discount %" : "Discount (BDT)"}
                        </label>
                        <input
                            type="number"
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                            placeholder={form.discount_type === "percentage" ? "e.g. 20" : "e.g. 100"}
                            min={1}
                            max={form.discount_type === "percentage" ? 100 : undefined}
                            value={form.discount_value}
                            onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Max uses (0 = unlimited)</label>
                        <input
                            type="number"
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40"
                            min={0}
                            value={form.max_uses}
                            onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                        />
                    </div>
                    <div className="flex items-center gap-3 pt-6">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}/>
                            <div className="w-10 h-5 bg-gray-200 peer-checked:bg-coral rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5"/>
                        </label>
                        <span className="text-[13px] text-ink-muted">Active</span>
                    </div>
                </div>

                {/* Applicable to */}
                <div className="mb-5">
                    <label className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-2">Applies to</label>
                    <div className="flex flex-wrap gap-2">
                        {ALL_APPLICABLE.map(key => (
                            <button
                                key={key}
                                onClick={() => toggleApplicable(key)}
                                className={`px-3 py-1 rounded-full text-[12px] font-semibold transition-colors border ${
                                    form.applicable_to.includes(key)
                                        ? "bg-coral text-white border-coral"
                                        : "bg-white text-ink-muted border-cream-300 hover:border-ink/30"
                                }`}
                            >
                                {key}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-ink-muted mt-1.5">Select "all" to apply to every product, or pick specific plans/bundles.</p>
                </div>

                <button
                    onClick={handleCreate}
                    disabled={saving}
                    className="flex items-center gap-2 bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors disabled:opacity-50"
                >
                    {saving ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>}
                    Create coupon
                </button>
            </div>

            {/* List */}
            <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-hidden">
                <div className="px-6 py-4 border-b border-cream-300 flex items-center gap-2">
                    <Tag size={15} className="text-coral"/>
                    <span className="font-semibold text-[14px]">All coupons ({coupons.length})</span>
                </div>
                {loading ? (
                    <div className="flex items-center gap-3 p-8 text-ink-muted"><Loader2 className="animate-spin" size={18}/> Loading…</div>
                ) : coupons.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted text-[14px]">No coupons yet. Create one above.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]">
                            <thead className="bg-cream-100">
                                <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-ink-muted font-semibold">
                                    <th className="px-5 py-3">Code</th>
                                    <th className="px-4 py-3">Discount</th>
                                    <th className="px-4 py-3">Applies to</th>
                                    <th className="px-4 py-3">Uses</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-cream-300">
                                {coupons.map(c => (
                                    <tr key={c.code} className="hover:bg-cream-100">
                                        <td className="px-5 py-3 font-mono font-bold text-ink">{c.code}</td>
                                        <td className="px-4 py-3">
                                            {c.discount_type === "percentage"
                                                ? <span className="text-coral font-semibold">{c.discount_value}%</span>
                                                : <span className="text-coral font-semibold">৳{c.discount_value}</span>}
                                        </td>
                                        <td className="px-4 py-3 max-w-[200px]">
                                            <div className="flex flex-wrap gap-1">
                                                {(c.applicable_to || []).map(a => (
                                                    <span key={a} className="inline-block bg-cream-100 border border-cream-300 rounded-full px-2 py-0.5 text-[10px] font-semibold">{a}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-ink-muted">
                                            {c.uses_count || 0}{c.max_uses > 0 ? `/${c.max_uses}` : ""}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => toggleActive(c.code, c.active)}
                                                className={`inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full transition-colors ${
                                                    c.active ? "bg-mint/30 text-ink hover:bg-red-100 hover:text-red-600" : "bg-gray-100 text-gray-500 hover:bg-mint/30 hover:text-ink"
                                                }`}
                                            >
                                                <span className={`w-1.5 h-1.5 rounded-full ${c.active ? "bg-mint" : "bg-gray-400"}`}/>
                                                {c.active ? "Active" : "Inactive"}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => handleDelete(c.code)}
                                                disabled={deletingCode === c.code}
                                                className="p-1.5 rounded-lg hover:bg-red-50 text-ink-muted hover:text-red-500 transition-colors"
                                            >
                                                {deletingCode === c.code ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function SupportTab() {
    const [tickets, setTickets] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [reply, setReply] = useState("");
    const [status, setStatus] = useState("open");
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadTickets = async () => {
        try {
            const { data } = await api.get("/admin/support/tickets");
            setTickets(data || []);
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
    };

    const openTicket = async (id) => {
        setSelected(id);
        setDetail(null);
        try {
            const { data } = await api.get(`/admin/support/tickets/${id}`);
            setDetail(data);
            setStatus(data?.ticket?.status || "open");
        } catch (e) {
            toast.error(getErrorMessage(e));
        }
    };

    useEffect(() => {
        loadTickets();
    }, []);

    const sendReply = async (e) => {
        e.preventDefault();
        if (!selected || (!reply.trim() && images.length === 0)) return;
        const fd = new FormData();
        fd.append("message", reply.trim());
        Array.from(images).forEach((f) => fd.append("images", f));
        setLoading(true);
        try {
            await api.post(`/admin/support/tickets/${selected}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setReply("");
            setImages([]);
            await openTicket(selected);
            await loadTickets();
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async () => {
        if (!selected) return;
        setLoading(true);
        try {
            await api.patch(`/admin/support/tickets/${selected}/status`, { status });
            toast.success("Ticket status updated");
            await openTicket(selected);
            await loadTickets();
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">Support tickets</h2>
                <button onClick={loadTickets} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-hidden">
                    <div className="px-4 py-3 border-b border-cream-300 text-[12px] uppercase tracking-[0.16em] font-semibold text-ink-muted">Tickets</div>
                    <ul className="divide-y divide-cream-300 max-h-[620px] overflow-y-auto">
                        {tickets.map((t) => (
                            <li key={t.id}>
                                <button onClick={() => openTicket(t.id)} className={`w-full text-left px-4 py-3 hover:bg-cream-100 ${selected === t.id ? "bg-cream-100" : ""}`}>
                                    <p className="text-[14px] font-semibold truncate">{t.subject}</p>
                                    <p className="text-[11px] text-ink-muted">{t.user_id} · {t.status}</p>
                                </button>
                            </li>
                        ))}
                        {tickets.length === 0 && <li className="px-4 py-8 text-[13px] text-ink-muted text-center">No tickets yet.</li>}
                    </ul>
                </div>

                <div className="xl:col-span-2 bg-white rounded-2xl border border-cream-300 shadow-soft min-h-[620px] flex flex-col">
                    {!selected ? (
                        <div className="p-8 text-ink-muted text-[14px]">Select a ticket to reply.</div>
                    ) : !detail ? (
                        <div className="p-8 text-ink-muted inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading...</div>
                    ) : (
                        <>
                            <div className="px-5 py-4 border-b border-cream-300 flex flex-wrap items-center gap-3">
                                <div className="flex-1">
                                    <p className="font-semibold text-[15px]">{detail.ticket?.subject}</p>
                                    <p className="text-[12px] text-ink-muted">Owner: {detail.ticket?.user_id}</p>
                                </div>
                                <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-cream-300 rounded-xl px-3 py-2 text-[12px]">
                                    <option value="open">open</option>
                                    <option value="pending">pending</option>
                                    <option value="closed">closed</option>
                                </select>
                                <button onClick={updateStatus} disabled={loading} className="bg-cream-100 border border-cream-300 rounded-xl px-3 py-2 text-[12px] font-semibold hover:bg-ink hover:text-cream-100">Update status</button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                                {(detail.messages || []).map((m) => (
                                    <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.sender_role === "admin" ? "bg-ink text-cream-100 ml-auto" : "bg-cream-100 text-ink"}`}>
                                        {m.message && <p className="text-[13px] whitespace-pre-wrap">{m.message}</p>}
                                        {Array.isArray(m.images) && m.images.length > 0 && (
                                            <div className="mt-2 grid grid-cols-2 gap-2">
                                                {m.images.map((img) => (
                                                    <a key={img} href={`/api/support/files/${img}`} target="_blank" rel="noreferrer">
                                                        <img src={`/api/support/files/${img}`} alt="attachment" className="w-full h-24 object-cover rounded-lg border border-black/10" />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                        <p className={`text-[10px] mt-1 ${m.sender_role === "admin" ? "text-cream-100/70" : "text-ink-muted"}`}>{m.created_at ? new Date(m.created_at).toLocaleString("en-BD") : ""}</p>
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={sendReply} className="p-4 border-t border-cream-300 space-y-2">
                                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]" placeholder="Reply to this ticket" />
                                <div className="flex items-center justify-between gap-3">
                                    <input type="file" accept="image/*" multiple onChange={(e) => setImages(e.target.files || [])} className="text-[12px]" />
                                    <button disabled={loading || (!reply.trim() && images.length === 0)} className="bg-ink hover:bg-coral text-cream-100 rounded-full px-4 py-2 text-[13px] font-semibold disabled:opacity-50">
                                        {loading ? <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin"/> Sending...</span> : "Send reply"}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Payments tab ─────────────────────────────────────────────
const STATUS_FILTERS = ["all", "pending", "hold", "completed", "failed", "error", "amount_mismatch", "transaction_mismatch"];
const STATUS_BADGE = {
    pending:              { label: "Pending",     cls: "bg-yellow-100 text-yellow-700",   icon: Clock },
    hold:                 { label: "Hold",         cls: "bg-orange-100 text-orange-700",   icon: Clock },
    processing:           { label: "Processing",   cls: "bg-blue-100 text-blue-700",       icon: Loader2 },
    completed:            { label: "Completed",    cls: "bg-mint/20 text-green-700",       icon: CheckCircle2 },
    failed:               { label: "Failed",       cls: "bg-red-100 text-red-600",         icon: XCircle },
    error:                { label: "Error",        cls: "bg-red-100 text-red-600",         icon: AlertCircle },
    amount_mismatch:      { label: "Amt mismatch", cls: "bg-purple-100 text-purple-700",  icon: AlertCircle },
    transaction_mismatch: { label: "Txn mismatch", cls: "bg-purple-100 text-purple-700",  icon: AlertCircle },
};
const TYPE_BADGE = {
    plan:   "bg-coral/20 text-coral",
    topup:  "bg-purple-100 text-purple-700",
    custom: "bg-blue-100 text-blue-700",
};

function StatusBadge({ status }) {
    const s = STATUS_BADGE[status] || { label: status, cls: "bg-gray-100 text-gray-600", icon: AlertCircle };
    const Icon = s.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${s.cls}`}>
            <Icon size={9}/> {s.label}
        </span>
    );
}

function PaymentsTab() {
    const [items, setItems]       = useState([]);
    const [total, setTotal]       = useState(0);
    const [page, setPage]         = useState(1);
    const [status, setStatus]     = useState("all");
    const [q, setQ]               = useState("");
    const [loading, setLoading]   = useState(true);
    const [acting, setActing]     = useState(null); // intent_id being acted on
    const LIMIT = 30;

    const load = useCallback(async (pg = page, st = status, query = q) => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/payments", {
                params: { status: st, q: query, page: pg, limit: LIMIT },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, [page, status, q]);

    useEffect(() => { load(1, status, q); }, [status]); // eslint-disable-line
    useEffect(() => { setPage(1); }, [status]);

    const doVerify = async (intentId) => {
        setActing(intentId + ":verify");
        try {
            const { data } = await api.post(`/admin/payments/${intentId}/verify`);
            if (data.ok && data.status === "completed") {
                toast.success("Payment verified and activated!");
            } else {
                toast.info(`Gateway status: ${data.gateway_status || data.status || "pending"}`);
            }
            await load(page, status, q);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setActing(null); }
    };

    const doConfirm = async (intentId) => {
        if (!window.confirm("Manually confirm this payment and grant credits/plan without gateway verification?")) return;
        setActing(intentId + ":confirm");
        try {
            await api.post(`/admin/payments/${intentId}/confirm`);
            toast.success("Payment confirmed and package activated!");
            await load(page, status, q);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setActing(null); }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">Payments</h2>
                <button onClick={() => load(page, status, q)} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                {STATUS_FILTERS.map(s => (
                    <button key={s} onClick={() => setStatus(s)}
                        className={`px-3 py-1 rounded-full text-[12px] font-semibold capitalize transition-all ${status === s ? "bg-ink text-cream-100" : "bg-white border border-cream-300 text-ink-muted hover:text-ink"}`}>
                        {s === "all" ? "All" : (STATUS_BADGE[s]?.label || s)}
                    </button>
                ))}
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"/>
                <input
                    type="text"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { setPage(1); load(1, status, q); } }}
                    placeholder="Search email, invoice ID, intent ID…"
                    className="w-full pl-8 pr-3 py-2 border border-cream-300 rounded-xl text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/30"
                />
            </div>

            {/* Stats strip */}
            <div className="text-[12px] text-ink-muted">
                {total.toLocaleString()} payment{total !== 1 ? "s" : ""} · page {page} / {totalPages}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-x-auto">
                {loading ? (
                    <div className="flex items-center gap-3 p-10 text-ink-muted"><Loader2 className="animate-spin" size={18}/> Loading…</div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted text-[14px]">No payments found</div>
                ) : (
                    <table className="w-full text-left text-[13px]">
                        <thead>
                            <tr className="border-b border-cream-300 text-[11px] uppercase tracking-[0.15em] text-ink-muted">
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Amount</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Invoice</th>
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(p => {
                                const isActingVerify  = acting === p.intent_id + ":verify";
                                const isActingConfirm = acting === p.intent_id + ":confirm";
                                const isCompleted     = p.status === "completed";
                                const canAct          = !isCompleted && !acting;
                                return (
                                    <tr key={p.intent_id} className="border-b border-cream-100 hover:bg-cream-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-ink">{p.email || "—"}</p>
                                            {p.name && <p className="text-[11px] text-ink-muted">{p.name}</p>}
                                            <p className="text-[10px] font-mono text-ink-muted/70 mt-0.5">{p.intent_id}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${TYPE_BADGE[p.type] || "bg-gray-100 text-gray-600"}`}>
                                                {p.type}
                                            </span>
                                            {p.type === "plan" && p.plan && (
                                                <p className="text-[11px] text-ink-muted mt-0.5 capitalize">{p.plan}</p>
                                            )}
                                            {p.type === "topup" && p.bundle_id && (
                                                <p className="text-[11px] text-ink-muted mt-0.5">{p.bundle_id}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 font-mono font-semibold">
                                            ৳{(p.amount_bdt ?? 0).toLocaleString()}
                                            {p.coupon_code && (
                                                <p className="text-[10px] text-green-600 font-sans">Coupon: {p.coupon_code}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge status={p.status}/>
                                            {p.gateway_status && p.gateway_status !== "COMPLETED" && (
                                                <p className="text-[10px] text-ink-muted mt-1">GW: {p.gateway_status}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="font-mono text-[11px] text-ink-muted">{p.invoice_id || "—"}</p>
                                        </td>
                                        <td className="px-4 py-3 text-ink-muted text-[12px] whitespace-nowrap">
                                            {fmtDate(p.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {/* Verify with Paymently */}
                                                {!isCompleted && p.invoice_id && (
                                                    <button
                                                        onClick={() => doVerify(p.intent_id)}
                                                        disabled={!canAct}
                                                        className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 border border-blue-200 px-2 py-1 rounded-lg hover:bg-blue-50 disabled:opacity-40 transition">
                                                        {isActingVerify ? <Loader2 size={10} className="animate-spin"/> : <RefreshCw size={10}/>}
                                                        Verify
                                                    </button>
                                                )}
                                                {/* Manual confirm */}
                                                {!isCompleted && (
                                                    <button
                                                        onClick={() => doConfirm(p.intent_id)}
                                                        disabled={!canAct}
                                                        className="flex items-center gap-1 text-[11px] font-semibold text-green-700 border border-green-200 px-2 py-1 rounded-lg hover:bg-green-50 disabled:opacity-40 transition">
                                                        {isActingConfirm ? <Loader2 size={10} className="animate-spin"/> : <Check size={10}/>}
                                                        Confirm
                                                    </button>
                                                )}
                                                {isCompleted && (
                                                    <span className="text-[11px] text-green-600 font-semibold flex items-center gap-1">
                                                        <CheckCircle2 size={12}/> Done
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center gap-2 justify-center">
                    <button disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p, status, q); }}
                        className="p-2 rounded-xl border border-cream-300 hover:bg-cream-100 disabled:opacity-40 transition">
                        <ChevronLeft size={14}/>
                    </button>
                    <span className="text-[13px] text-ink-muted">Page {page} of {totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(p, status, q); }}
                        className="p-2 rounded-xl border border-cream-300 hover:bg-cream-100 disabled:opacity-40 transition">
                        <ChevronRight size={14}/>
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── API Keys tab ────────────────────────────────────────────
function ApiKeysTab() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [deleting, setDeleting] = useState(null);
    const LIMIT = 25;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/api-keys`, { params: { q, page, limit: LIMIT } });
            setKeys(data.keys || []);
            setTotal(data.total || 0);
        } catch(e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, [q, page]);

    useEffect(() => { load(); }, [load]);

    const deleteKey = async (kid, name) => {
        if (!window.confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
        setDeleting(kid);
        try {
            await api.delete(`/admin/api-keys/${kid}`);
            toast.success("API key revoked");
            load();
        } catch(e) { toast.error(getErrorMessage(e)); }
        finally { setDeleting(null); }
    };

    const totalPages = Math.ceil(total / LIMIT);

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">API Keys</h2>
                <button onClick={load} className="flex items-center gap-2 text-[13px] text-ink-muted hover:text-ink px-3 py-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                    <RefreshCw size={13}/> Refresh
                </button>
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"/>
                <input
                    value={q}
                    onChange={e => { setQ(e.target.value); setPage(1); }}
                    placeholder="Search by email or domain…"
                    className="w-full pl-8 pr-3 py-2 rounded-xl border border-cream-300 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-coral/30"
                />
            </div>

            {loading ? (
                <div className="flex items-center gap-3 p-10 text-ink-muted"><Loader2 className="animate-spin" size={20}/> Loading…</div>
            ) : (
                <div className="bg-white rounded-2xl border border-cream-300 shadow-soft overflow-hidden">
                    <table className="w-full text-[13px]">
                        <thead className="bg-cream-100 border-b border-cream-300">
                            <tr>
                                {["Key","User","Domain","Type","Last used","Created",""].map(h => (
                                    <th key={h} className="text-left px-4 py-3 font-semibold text-ink-muted text-[11px] uppercase tracking-wide">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-cream-200">
                            {keys.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-12 text-ink-muted">No API keys found.</td></tr>
                            )}
                            {keys.map(k => (
                                <tr key={k._id} className="hover:bg-cream-50 transition">
                                    <td className="px-4 py-3">
                                        <span className="font-mono text-[12px] bg-cream-100 px-2 py-1 rounded-lg">{k.secret?.slice(0,16)}…</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium">{k.user_name || "—"}</div>
                                        <div className="text-ink-muted text-[11px]">{k.user_email || k.user_id}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {k.domain ? (
                                            <span className="flex items-center gap-1 text-ink-muted"><Globe size={12}/>{k.domain}</span>
                                        ) : <span className="text-ink-muted">—</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        {k.key_type === "wordpress" ? (
                                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700">WordPress</span>
                                        ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">Manual</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-ink-muted">
                                        {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "Never"}
                                    </td>
                                    <td className="px-4 py-3 text-ink-muted">
                                        {new Date(k.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => deleteKey(k._id, k.name)}
                                            disabled={deleting === k._id}
                                            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-red-500 hover:bg-red-50 border border-red-200 text-[12px] disabled:opacity-40 transition"
                                        >
                                            {deleting === k._id ? <Loader2 size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                                            Revoke
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-cream-200 text-[13px]">
                            <span className="text-ink-muted">{total} total keys</span>
                            <div className="flex items-center gap-2">
                                <button disabled={page === 1} onClick={() => setPage(p => p-1)}
                                    className="p-1.5 rounded-lg hover:bg-cream-100 disabled:opacity-40 transition">
                                    <ChevronLeft size={15}/>
                                </button>
                                <span>Page {page} of {totalPages}</span>
                                <button disabled={page === totalPages} onClick={() => setPage(p => p+1)}
                                    className="p-1.5 rounded-lg hover:bg-cream-100 disabled:opacity-40 transition">
                                    <ChevronRight size={15}/>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function SecurityTab() {
    const [form, setForm] = useState({ current_password: "", new_password: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.current_password || !form.new_password) {
            toast.error("Current and new password are required");
            return;
        }
        setLoading(true);
        try {
            await api.post("/admin/change-password", form);
            setForm({ current_password: "", new_password: "" });
            toast.success("Admin password changed");
        } catch (e) {
            toast.error(getErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-xl">
            <h2 className="font-display text-[26px] font-semibold tracking-tight mb-4">Admin security</h2>
            <form onSubmit={submit} className="bg-white rounded-2xl border border-cream-300 shadow-soft p-6 space-y-3">
                <input
                    type="password"
                    value={form.current_password}
                    onChange={(e) => setForm((p) => ({ ...p, current_password: e.target.value }))}
                    placeholder="Current password"
                    className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                />
                <input
                    type="password"
                    value={form.new_password}
                    onChange={(e) => setForm((p) => ({ ...p, new_password: e.target.value }))}
                    placeholder="New password"
                    className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                />
                <button disabled={loading} className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-2.5 text-[13px] font-semibold disabled:opacity-70">
                    {loading ? "Updating..." : "Change password"}
                </button>
            </form>
        </div>
    );
}

// ─── Monetize tab ─────────────────────────────────────────────
function MonetizeTab() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [reviewStatus, setReviewStatus] = useState("");
    const [rejectionReason, setRejectionReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try { setApps((await api.get("/admin/monetize")).data); }
        catch (e) { toast.error(getErrorMessage(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doReview = async () => {
        if (!selected || !reviewStatus) return;
        if (reviewStatus === "rejected" && !rejectionReason.trim()) {
            toast.error("Please provide a rejection reason."); return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/monetize/${selected.id}`, {
                status: reviewStatus,
                rejection_reason: reviewStatus === "rejected" ? rejectionReason.trim() : null,
            });
            toast.success(`Application ${reviewStatus}.`);
            setSelected(null); setReviewStatus(""); setRejectionReason("");
            await load();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setSaving(false); }
    };

    const STATUS_COLORS = {
        pending: "bg-amber-100 text-amber-700",
        approved: "bg-mint/30 text-ink",
        rejected: "bg-red-100 text-red-700",
    };

    const filtered = filter === "all" ? apps : apps.filter(a => a.status === filter);

    if (loading) return <div className="flex items-center gap-3 p-10 text-ink-muted"><Loader2 className="animate-spin" size={20}/> Loading…</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-display text-[26px] font-semibold tracking-tight">Monetize Applications</h2>
                <div className="flex items-center gap-2">
                    {["all","pending","approved","rejected"].map(f => (
                        <button key={f} onClick={() => setFilter(f)}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold capitalize transition ${filter === f ? "bg-ink text-cream-100" : "bg-cream-100 border border-cream-300 text-ink hover:bg-cream-200"}`}>
                            {f}
                        </button>
                    ))}
                    <button onClick={load} className="ml-2 p-1.5 rounded-xl border border-cream-300 hover:bg-cream-100 transition">
                        <RefreshCw size={14}/>
                    </button>
                </div>
            </div>

            {filtered.length === 0 && (
                <div className="bg-cream-100 rounded-3xl p-10 text-center text-ink-muted text-[14px]">No applications {filter !== "all" ? `with status "${filter}"` : "yet"}.</div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filtered.map(app => (
                    <div key={app.id} onClick={() => { setSelected(app); setReviewStatus(""); setRejectionReason(""); }}
                        className={`bg-white rounded-3xl border p-5 cursor-pointer hover:shadow-soft-lg transition-all ${selected?.id === app.id ? "border-coral shadow-soft-lg" : "border-cream-300"}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <p className="font-semibold text-[15px]">{app.user_name || "—"}</p>
                                <p className="text-[12px] text-ink-muted">{app.user_email}</p>
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_COLORS[app.status] || "bg-gray-100 text-gray-700"}`}>{app.status}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-[12px] text-ink-muted mb-3">
                            {app.daily_audience && <span>Daily: <strong>{app.daily_audience.toLocaleString()}</strong></span>}
                            {app.monthly_audience && <span>Monthly: <strong>{app.monthly_audience.toLocaleString()}</strong></span>}
                            {app.contact_no && <span>📞 {app.contact_no}</span>}
                        </div>
                        {app.website_url && <a href={app.website_url} target="_blank" rel="noreferrer" className="text-[12px] text-coral hover:underline">{app.website_url}</a>}
                        <p className="mt-2 text-[12px] text-ink-muted line-clamp-2">{app.description}</p>
                        <p className="mt-2 text-[11px] text-ink-muted">Submitted {app.created_at ? new Date(app.created_at).toLocaleDateString("en-BD") : "—"}</p>
                    </div>
                ))}
            </div>

            {/* Review panel */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-cream-300 flex items-center justify-between">
                            <div>
                                <p className="font-semibold text-[16px]">{selected.user_name || selected.user_email}</p>
                                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_COLORS[selected.status] || "bg-gray-100"}`}>{selected.status}</span>
                            </div>
                            <button onClick={() => setSelected(null)} className="p-2 rounded-xl hover:bg-cream-100"><X size={16}/></button>
                        </div>
                        <div className="p-6 space-y-4 text-[14px]">
                            {selected.website_url && <Detail label="Website" value={<a href={selected.website_url} target="_blank" rel="noreferrer" className="text-coral hover:underline">{selected.website_url}</a>}/>}
                            {selected.youtube_url && <Detail label="YouTube" value={<a href={selected.youtube_url} target="_blank" rel="noreferrer" className="text-coral hover:underline">{selected.youtube_url}</a>}/>}
                            {selected.social_urls && <Detail label="Other socials" value={selected.social_urls}/>}
                            {selected.daily_audience && <Detail label="Daily audience" value={selected.daily_audience.toLocaleString()}/>}
                            {selected.monthly_audience && <Detail label="Monthly audience" value={selected.monthly_audience.toLocaleString()}/>}
                            {selected.yearly_audience && <Detail label="Yearly audience" value={selected.yearly_audience.toLocaleString()}/>}
                            {selected.contact_no && <Detail label="Contact" value={selected.contact_no}/>}
                            <div>
                                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-1.5">Description</p>
                                <p className="whitespace-pre-wrap bg-cream-100 rounded-xl p-3 text-[13px]">{selected.description}</p>
                            </div>
                            {selected.status === "rejected" && selected.rejection_reason && (
                                <Detail label="Previous rejection reason" value={selected.rejection_reason}/>
                            )}

                            {selected.status !== "approved" && (
                                <div className="pt-4 border-t border-cream-300 space-y-3">
                                    <p className="text-[12px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Review decision</p>
                                    <div className="flex gap-3">
                                        <button onClick={() => setReviewStatus("approved")}
                                            className={`flex-1 py-2 rounded-xl text-[13px] font-semibold border transition ${reviewStatus === "approved" ? "bg-mint/30 border-mint text-ink" : "border-cream-300 hover:bg-cream-100"}`}>
                                            ✓ Approve
                                        </button>
                                        <button onClick={() => setReviewStatus("rejected")}
                                            className={`flex-1 py-2 rounded-xl text-[13px] font-semibold border transition ${reviewStatus === "rejected" ? "bg-red-50 border-red-300 text-red-700" : "border-cream-300 hover:bg-cream-100"}`}>
                                            ✗ Reject
                                        </button>
                                    </div>
                                    {reviewStatus === "rejected" && (
                                        <textarea rows={3} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                                            placeholder="Reason for rejection (shown to user)…"
                                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-coral/40 resize-none" />
                                    )}
                                    {reviewStatus && (
                                        <button onClick={doReview} disabled={saving}
                                            className="w-full bg-ink hover:bg-coral text-cream-100 rounded-full py-2.5 text-[13px] font-semibold transition disabled:opacity-60 inline-flex items-center justify-center gap-2">
                                            {saving ? <><Loader2 size={13} className="animate-spin"/> Saving…</> : `Submit ${reviewStatus}`}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-ink-muted mb-0.5">{label}</p>
            <p className="text-[14px] text-ink">{value}</p>
        </div>
    );
}

export default function AdminPanel() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("dashboard");
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    if (!user) return null;
    if (user.role !== "admin") {
        return (
            <div className="min-h-screen bg-cream-100 flex items-center justify-center">
                <div className="bg-white rounded-3xl p-10 text-center shadow-soft border border-cream-300 max-w-sm">
                    <AlertTriangle size={32} className="text-coral mx-auto mb-4"/>
                    <h2 className="font-display text-[22px] font-semibold mb-2">Access denied</h2>
                    <p className="text-[14px] text-ink-muted mb-4">This area is restricted to admins.</p>
                    <button onClick={() => navigate("/dashboard")} className="bg-ink text-cream-100 rounded-full px-6 py-2.5 text-[13px] font-semibold">
                        Back to dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream-100 flex">
            {/* Sidebar */}
            <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-[240px] bg-ink text-cream-100 flex flex-col p-4 transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
                <div className="flex items-center gap-2.5 px-3 py-3 mb-2">
                    <WaveIcon color="#FF5B3E" className="w-6 h-4"/>
                    <span className="font-display text-[18px] font-semibold tracking-tight">Admin</span>
                </div>
                <div className="mx-1 mb-4 bg-white/10 rounded-xl px-3 py-2 flex items-center gap-2">
                    <Shield size={12} className="text-coral"/>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-cream-100/70">Admin panel</span>
                </div>

                <nav className="flex-1 space-y-1">
                    {TABS.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => { setTab(t.id); setMobileOpen(false); }}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors text-left ${tab === t.id ? "bg-coral text-white" : "text-cream-100/70 hover:bg-white/10 hover:text-cream-100"}`}>
                                <Icon size={16}/>{t.label}
                            </button>
                        );
                    })}
                </nav>

                <div className="mt-3 pt-3 border-t border-white/10 space-y-1">
                    <button onClick={() => navigate("/dashboard")}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-cream-100/60 hover:bg-white/10 hover:text-cream-100 transition">
                        <ArrowLeft size={15}/> Back to app
                    </button>
                    <button onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] text-cream-100/60 hover:bg-white/10 hover:text-cream-100 transition">
                        <LogOut size={15}/> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile overlay */}
            {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)}/>}

            {/* Main */}
            <main className="flex-1 min-w-0">
                {/* Top bar */}
                <header className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-cream-300 px-6 py-3 flex items-center gap-3">
                    <button onClick={() => setMobileOpen(o => !o)} className="lg:hidden p-2 rounded-xl hover:bg-cream-100 transition">
                        <Activity size={16}/>
                    </button>
                    <span className="text-[13px] font-semibold text-ink capitalize">{TABS.find(t => t.id === tab)?.label}</span>
                    <div className="ml-auto flex items-center gap-2 text-[12px] text-ink-muted">
                        <span className="bg-coral/20 text-coral font-bold text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full">admin</span>
                        <span>{user.name || user.email}</span>
                    </div>
                </header>

                <div className="p-6 max-w-7xl mx-auto">
                    {tab === "dashboard" && <DashboardTab/>}
                    {tab === "users" && <UsersTab/>}
                    {tab === "articles" && <ArticlesTab/>}
                    {tab === "payments" && <PaymentsTab/>}
                    {tab === "coupons" && <CouponsTab/>}
                    {tab === "support" && <SupportTab/>}
                    {tab === "security" && <SecurityTab/>}
                    {tab === "api_keys" && <ApiKeysTab/>}
                    {tab === "monetize" && <MonetizeTab/>}
                </div>
            </main>
        </div>
    );
}
